import { graphql } from 'gatsby';
import React, { createContext } from 'react';
import { useChain, useSpringRef, SpringRef } from 'react-spring';
import { useProductVariants, useProductBySlug } from 'utils/hooks';
import { PageSectionType } from 'utils/types';

import Layout from '../components/Layout';
import { SEO } from '../components/SEO';
import {
  DisclaimerSectionContainer as MarkdownSection,
  DisclaimerSectionContainerProps,
} from '../containers/Enterprise/DisclaimerSectionContainer';
import {
  ExperienceContainer as ExperienceSection,
  ExperienceContainerProps,
} from '../containers/Enterprise/ExperienceContainer';
import {
  HeroContainer as HeroSection,
  HeroContainerProps,
} from '../containers/Enterprise/HeroContainer';
import {
  InsightsContainer as InsightsSection,
  InsightsContainerProps,
} from '../containers/Enterprise/InsightsContainer';
import {
  InTheNewsContainer as InTheNewsSection,
  InTheNewsContainerProps,
} from '../containers/Enterprise/InTheNewsContainer';
import {
  OutreachContainer as OutreachSection,
  OutreachContainerProps,
} from '../containers/Enterprise/OutreachContainer';
import {
  PartnerContainer as PartnerSection,
  PartnerContainerProps,
} from '../containers/Enterprise/PartnerContainer';

const components: {
  HeroSection: React.FunctionComponent<HeroContainerProps>;
  ExperienceSection: React.FunctionComponent<ExperienceContainerProps>;
  OutreachSection: React.FunctionComponent<OutreachContainerProps>;
  InTheNewsSection: React.FunctionComponent<InTheNewsContainerProps>;
  InsightsSection: React.FunctionComponent<InsightsContainerProps>;
  PartnerSection: React.FunctionComponent<PartnerContainerProps>;
  MarkdownSection: React.FunctionComponent<DisclaimerSectionContainerProps>;
} = {
  HeroSection,
  ExperienceSection,
  OutreachSection,
  InTheNewsSection,
  InsightsSection,
  PartnerSection,
  MarkdownSection,
};

interface PageContext {
  componentName: string;
  canonicalUrl: string;
}

interface Props {
  pageContext: PageContext;
  data: any;
}

export interface RefsContextType {
  [key: string]: SpringRef;
}

export const RefsForAnimationContext = createContext<RefsContextType>({});

const enterpriseTemplate: React.FC<Props> = ({ data, pageContext }) => {
  const { contentfulPage } = data;
  const { sections, heroSection } = contentfulPage;
  const productVariants = useProductVariants();
  const productsBySlug = useProductBySlug(productVariants);
  const { seoHeader } = contentfulPage;

  if (!sections || !heroSection) {
    return null;
  }

  let SEOprops = {
    canonicalUrl: pageContext.canonicalUrl,
    title: contentfulPage.title,
    description: '',
  };
  if(seoHeader) {
    SEOprops.canonicalUrl = seoHeader.canonicalUrl
    SEOprops.description = seoHeader.metaDescription?.metaDescription
  }

  // Refs will be passed down in RefsForAnimationContext
  const animation1 = useSpringRef();
  const animation2 = useSpringRef();

  // by default useChain will space each animation 500ms apart
  // the 2nd argument overrides this default and defines the timing of the animations:
  // 1st animation happens immediately - 2nd animation starts 450ms after first animation ends
  // 3rd and 4th animations start 750ms after the first animation ends
  useChain([animation1, animation2], [0, 0.2]);

  return (
    <>
      <SEO { ...SEOprops } />
      <Layout productsBySlug={productsBySlug}>
        <RefsForAnimationContext.Provider value={{ animation1, animation2 }}>
          <HeroSection content={heroSection} />
          {sections.map((section: PageSectionType, i: number) => {
            // @ts-ignore
            if (components[section.componentName]) {
              // @ts-ignore
              return React.createElement(components[section.componentName], {
                content: section,
                key: `${section.componentName}-${i}`, // handles the case for duplicate section keys
              });
            }
          })}
        </RefsForAnimationContext.Provider>
      </Layout>
    </>
  );
};

export default enterpriseTemplate;

export const query = graphql`
  query ($pageId: String, $imageWidth: Int) {
    contentfulPage(pageId: { eq: $pageId }) {
      title
      seoHeader {
        metaDescription {
          metaDescription
        }
        canonicalUrl
      }
      heroSection {
        backgroundColor
        componentName
        headline
        desktopImage {
          description
          gatsbyImageData(placeholder: BLURRED, quality: 75, width: $imageWidth)
        }
        mobileImage {
          description
          gatsbyImageData(placeholder: BLURRED, quality: 75)
        }
        mainCta {
          text
          url
        }
      }
      sections {
        componentName
        title
        headline
        subHeadline
        mainCta {
          text
          url
        }
        listOfContentWithImages {
          heading
          description {
            childMarkdownRemark {
              html
            }
            description
          }
          image {
            description
            gatsbyImageData(placeholder: BLURRED, width: 160, quality: 75)
          }
          cta {
            text
            url
          }
        }
        desktopImages {
          description
          gatsbyImageData(placeholder: BLURRED, quality: 75)
        }
        mobileImages {
          description
          gatsbyImageData(placeholder: BLURRED, quality: 75)
        }
      }
    }
  }
`;
